/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { OmitFunctions } from 'src/core/stores/types';
import { AutoTaggingJobSwagger } from 'src/customText/core/services/autoTagging/customTextAutoTaggingSwagger';
import { ModelJobStates } from 'src/modules/modelTraining/types/trainingTypes';

type AutoTaggingJobStoreData = OmitFunctions<Partial<Omit<AutoTaggingJobStore, 'id'>>>;

export class AutoTaggingJobStore {
    public id: string;

    public trainedModelLabel: string;

    public createdDateTime: Date;

    public expirationDateTime: Date;

    public elapsedTime: string;

    public documents: string[];

    public totalCharsCount: number;

    public extractors: string[];

    @observable public lastUpdatedDateTime: Date;

    @observable public status: ModelJobStates;

    @observable public percentage: number;

    @observable public errors: AutoTaggingJobSwagger['errors'];

    constructor(id: string, data?: AutoTaggingJobStoreData) {
        this.id = id;
        this.status = data?.status;
        this.trainedModelLabel = data?.trainedModelLabel;
        this.percentage = data?.percentage ?? 0;
        this.createdDateTime = data?.createdDateTime ?? new Date();
        this.expirationDateTime = data?.expirationDateTime ?? new Date();
        this.lastUpdatedDateTime = data?.lastUpdatedDateTime ?? new Date();
        this.errors = data?.errors;
        this.documents = data?.documents ?? [];
        this.extractors = data?.extractors ?? [];
        this.totalCharsCount = Math.ceil((data?.totalCharsCount ?? 0) / 1000);
        this.elapsedTime = data?.elapsedTime;
    }

    @action
    public updateData(data: AutoTaggingJobStoreData) {
        this.status = data.status;
        this.trainedModelLabel = data.trainedModelLabel;
        this.percentage = data.percentage ?? 0;
        this.createdDateTime = data.createdDateTime ?? new Date();
        this.expirationDateTime = data.expirationDateTime ?? new Date();
        this.lastUpdatedDateTime = data.lastUpdatedDateTime ?? new Date();
        this.documents = data.documents;
        this.extractors = data.extractors;
        this.totalCharsCount = Math.ceil((data?.totalCharsCount ?? 0) / 1000);
        this.elapsedTime = data.elapsedTime;
        if (data.errors) {
            this.errors = data.errors;
        }
    }

    @action
    public setStatus(status: ModelJobStates, errors?: AutoTaggingJobSwagger['errors']) {
        this.status = status;
        if (errors) {
            this.errors = errors;
        }
    }
}
